import { useMemo } from "react";
import { motion } from "framer-motion";
import { Trophy, TrendingUp } from "lucide-react";
import { AnimatedNumber } from "@/components/dashboard/AnimatedNumber";
import { useDashboard } from "@/hooks/useProgress";
import { cn } from "@/lib/cn";

/**
 * Summary of the user's quiz performance.
 *   - Best score, across every subject (from `subject_rollups`)
 *   - Average of the per-subject averages
 *   - Most recent score (whichever chapter was last quizzed)
 *   - A small 7-bar sparkline of best scores per chapter (most recent first)
 *
 * Best and average come from `subject_rollups`, which aggregates every touched
 * chapter. They used to be computed from `recent_chapters` -- five rows across
 * all subjects combined -- while this comment claimed "across all touched
 * chapters". The "most recent" tile and the sparkline still read
 * `recent_chapters`, which is exactly what that array is for.
 *
 * Renders nothing if the user has no quiz attempts yet (empty state
 * lives in the daily-question card -- two empty cards in a row would
 * be a downer).
 */
export function QuizPerformance() {
  const dashboard = useDashboard();
  const recent = useMemo(() => dashboard.data?.recent_chapters ?? [], [dashboard.data]);
  const rollups = dashboard.data?.subject_rollups ?? null;

  const data = useMemo(() => {
    const scored = recent.filter((p) => p.quiz_attempts > 0 && p.quiz_best_score > 0);
    const quizzedSubjects = (rollups ?? []).filter((r) => r.quiz_best_score != null);
    if (scored.length === 0 && quizzedSubjects.length === 0) return null;

    const best = quizzedSubjects.length
      ? Math.max(...quizzedSubjects.map((r) => r.quiz_best_score!))
      : Math.max(...scored.map((p) => p.quiz_best_score));
    const avg = quizzedSubjects.length
      ? Math.round(
          quizzedSubjects.reduce((sum, r) => sum + (r.quiz_avg_score ?? r.quiz_best_score!), 0) /
            quizzedSubjects.length,
        )
      : Math.round(scored.reduce((sum, p) => sum + p.quiz_best_score, 0) / scored.length);

    // Most recent score: pick the chapter with the latest quiz_last_attempted_at.
    const mostRecent = scored
      .filter((p) => p.quiz_last_attempted_at)
      .sort(
        (a, b) =>
          new Date(b.quiz_last_attempted_at!).getTime() -
          new Date(a.quiz_last_attempted_at!).getTime(),
      )[0];
    const last = mostRecent?.quiz_last_score ?? mostRecent?.quiz_best_score ?? scored[0]?.quiz_best_score ?? best;

    // Sparkline: best score per chapter, most-recently-accessed first.
    const sparks = recent
      .slice()
      .sort((a, b) => new Date(b.last_accessed_at).getTime() - new Date(a.last_accessed_at).getTime())
      .map((p) => p.quiz_best_score)
      .filter((s) => s > 0)
      .slice(0, 7)
      .reverse(); // oldest to newest for visual flow

    return { best, avg, last, sparks };
  }, [recent, rollups]);

  if (!data) return null;

  const trend = data.sparks.length >= 2 && data.sparks[data.sparks.length - 1] > data.sparks[0];

  return (
    <section>
      <header className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-sm font-semibold tracking-tight text-text-muted uppercase tracking-[0.14em]">
          Quiz performance
        </h3>
        {trend ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-success font-medium">
            <TrendingUp className="h-3 w-3" />
            Trending up
          </span>
        ) : null}
      </header>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="rounded-2xl border border-border bg-surface p-4 sm:p-5"
      >
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <Stat label="Best" value={data.best} highlight />
          <Stat label="Average" value={data.avg} />
          <Stat label="Last" value={data.last} />
        </div>
        <Sparkline scores={data.sparks} />
      </motion.div>
    </section>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-text-subtle">
        {highlight ? <Trophy className="h-3 w-3" /> : null}
        <span className="text-[10px] uppercase tracking-[0.12em]">{label}</span>
      </div>
      <div className={cn(
        "mt-1 font-display font-bold tabular-nums",
        highlight ? "text-2xl sm:text-3xl text-brand" : "text-xl sm:text-2xl",
      )}>
        <AnimatedNumber value={value} durationMs={520} format={(n) => `${Math.round(n)}%`} />
      </div>
    </div>
  );
}

function Sparkline({ scores }: { scores: number[] }) {
  if (scores.length === 0) return null;
  const max = Math.max(100, ...scores);
  return (
    <div className="mt-5 pt-4 border-t border-border">
      <p className="text-[10px] uppercase tracking-[0.12em] text-text-subtle mb-2">
        Recent chapters
      </p>
      <div className="flex items-end gap-1.5 h-12">
        {scores.map((score, i) => {
          const pct = Math.max(8, (score / max) * 100);
          const tone =
            score >= 80 ? "bg-success" :
            score >= 60 ? "bg-brand" :
            "bg-text-subtle/60";
          return (
            <motion.div
              key={i}
              className={cn("flex-1 rounded-sm", tone)}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: `${pct}%`, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
            />
          );
        })}
      </div>
    </div>
  );
}
